import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { catalystItems, getMediaType, getMediaUrl } from "./catalystItems";
import "./App.css";

const CatalystGallery = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const closeLightbox = useCallback(() => setActiveIndex(null), []);

  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight")
        setActiveIndex((i) => (i + 1) % catalystItems.length);
      if (e.key === "ArrowLeft")
        setActiveIndex((i) => (i - 1 + catalystItems.length) % catalystItems.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activeIndex, closeLightbox]);

  const activeItem = activeIndex !== null ? catalystItems[activeIndex] : null;

  return (
    <div className="app">
      <Helmet>
        <title>Catalyst — Sample Gallery | FirstMark Media</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <nav className="navbar">
        <div className="navbar-content">
          <div className="logo centered-logo">
            <Link to="/">
              <img src="/logo.png" alt="FirstMark Logo" className="logo-img" />
            </Link>
          </div>
          <ul className="nav-links">
            <li><Link to="/">Home</Link></li>
          </ul>
        </div>
      </nav>

      <section className="intro-section reveal active">
        <header className="intro-content">
          <h1>Catalyst — Sample Shots</h1>
          <p>A preview gallery of the sample photography and video we've put together. Click any tile to view full size.</p>
        </header>
      </section>

      <section className="gallery-section">
        <div className="gallery-grid">
          {catalystItems.map((item, index) => {
            const type = getMediaType(item.file);
            const url = getMediaUrl(item.file);
            return (
              <button
                key={item.file}
                className="gallery-tile"
                onClick={() => setActiveIndex(index)}
                aria-label={`View ${item.caption}`}
                type="button"
              >
                <div className="gallery-media-wrap">
                  {type === "image" && (
                    <img src={url} alt={item.caption} loading="lazy" />
                  )}
                  {type === "video" && (
                    <>
                      <video
                        src={url}
                        poster={item.poster ? getMediaUrl(item.poster) : undefined}
                        preload="metadata"
                        muted
                        playsInline
                      />
                      <div className="gallery-play-icon" aria-hidden="true">▶</div>
                    </>
                  )}
                </div>
                <span className="gallery-caption">{item.caption}</span>
              </button>
            );
          })}
        </div>

        {catalystItems.length === 0 && (
          <p className="gallery-empty">No items yet — add files to <code>public/catalyst/</code> and update <code>src/catalystItems.js</code>.</p>
        )}
      </section>

      {activeItem && (
        <div
          className="lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={activeItem.caption}
        >
          <button
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
            type="button"
          >×</button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {getMediaType(activeItem.file) === "image" ? (
              <img src={getMediaUrl(activeItem.file)} alt={activeItem.caption} />
            ) : (
              <video
                src={getMediaUrl(activeItem.file)}
                poster={activeItem.poster ? getMediaUrl(activeItem.poster) : undefined}
                controls
                autoPlay
                playsInline
              />
            )}
            <p className="lightbox-caption">{activeItem.caption}</p>
          </div>

          {catalystItems.length > 1 && (
            <>
              <button
                className="lightbox-nav lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((i) => (i - 1 + catalystItems.length) % catalystItems.length);
                }}
                aria-label="Previous"
                type="button"
              >‹</button>
              <button
                className="lightbox-nav lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((i) => (i + 1) % catalystItems.length);
                }}
                aria-label="Next"
                type="button"
              >›</button>
            </>
          )}
        </div>
      )}

      <footer className="footer">
        <p>
          Questions? Reach us at <a href="mailto:contact@firstmarkmedia.co.uk">contact@firstmarkmedia.co.uk</a>
        </p>
        <p>&copy; {new Date().getFullYear()} FirstMark Media. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default CatalystGallery;
